import type { UID } from '@strapi/strapi';
import axios from 'axios';
import type { APIResponse, APIResponseCollection } from 'common-types';
import qs from 'qs';
import { STRAPI_API_HOST } from 'services/api/config';
import { DEFAULT_LOCALE } from 'utils/constants';

const axiosClient = axios.create({ baseURL: `${STRAPI_API_HOST}/api` });

type TStrapiQueryParams = {
  locale?: string;
  populate?: Record<string, unknown>;
};

type TFetchLocalizedContentProps = {
  endpoint: string;
  id: string | null;
  locale?: string | null;
  populate?: Record<string, unknown>;
  signal?: AbortSignal;
};

const buildQueryParams = ({
  locale,
  populate,
}: {
  locale?: string | null;
  populate?: Record<string, unknown>;
}): TStrapiQueryParams => {
  const params: TStrapiQueryParams = {};

  if (locale && locale !== DEFAULT_LOCALE) {
    params.locale = locale;
  }

  if (populate) {
    params.populate = populate;
  }

  return params;
};

const makeRequest = async <T>({ url, signal }: { url: string; signal?: AbortSignal }): Promise<T> => {
  const response = await axiosClient.get<T>(url, { signal });

  return response.data;
};

const tryFetchByLocale = async <T extends UID.ContentType>({
  endpoint,
  locale,
  populate,
  signal,
}: {
  endpoint: string;
  locale: string;
  populate?: Record<string, unknown>;
  signal?: AbortSignal;
}): Promise<APIResponse<T> | null> => {
  const queryParams = buildQueryParams({ locale, populate });
  const queryString = qs.stringify(queryParams, { addQueryPrefix: true });

  try {
    const response = await makeRequest<APIResponseCollection<T>>({
      url: `${endpoint}${queryString}`,
      signal,
    });

    const firstItem = response.data?.[0];

    return firstItem ? { data: firstItem } : null;
  } catch {
    // Silently fail and let fallback handle the request
    return null;
  }
};

const fetchById = async <T extends UID.ContentType>({
  endpoint,
  id,
  locale,
  populate,
  signal,
}: TFetchLocalizedContentProps): Promise<APIResponse<T>> => {
  // Strapi v5: fetch single entities via a filter on `documentId` (string) or numeric `id`,
  // not the path param (GET /endpoint/:numericId 404s in v5).
  const filterKey = /^\d+$/.test(String(id)) ? 'id' : 'documentId';
  const queryParams = {
    ...buildQueryParams({ locale, populate }),
    filters: { [filterKey]: { $eq: id } },
    pagination: { pageSize: 1 },
  };
  const queryString = qs.stringify(queryParams, { addQueryPrefix: true });

  const response = await makeRequest<APIResponseCollection<T>>({
    url: `${endpoint}${queryString}`,
    signal,
  });

  return { data: response.data?.[0] } as APIResponse<T>;
};

export const fetchLocalizedContent = async <T extends UID.ContentType>({
  endpoint,
  id,
  locale,
  populate,
  signal,
}: TFetchLocalizedContentProps): Promise<APIResponse<T>> => {
  // For non-default locales, try to get content in that locale first
  if (locale && locale !== DEFAULT_LOCALE) {
    const localizedContent = await tryFetchByLocale<T>({
      endpoint,
      locale,
      populate,
      signal,
    });

    if (localizedContent) {
      return localizedContent;
    }
  }

  // Fallback to direct ID request
  return fetchById<T>({
    endpoint,
    id,
    locale,
    populate,
    signal,
  });
};
